import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext } from "@hello-pangea/dnd";
import { FaSave, FaDownload, FaEdit, FaEye, FaTimes, FaSpinner, FaExpand, FaCompress } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import Sidebar from "./Sidebar";
import FormCanvas from "./FormCanvas";
import FormPreview from "./FormPreview";
import { useForms } from "../../contexts/FormsContext";
import { useFormsMaster } from "../../contexts/FormsMasterContext";
import Notification from "../../components/UI/Notification";
import { SIDEBAR_DROPPABLE_ID } from "./Sidebar";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const FormBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { roles } = useContext(AuthContext);
    const { forms, createForm, updateForm, fetchForms, loading } = useForms();
    const { purposes } = useFormsMaster()
    const [error, setError] = useState("");

    const [activeInsertIndex, setActiveInsertIndex] = useState(null);
    const [fields, setFields] = useState([]);
    const [formName, setFormName] = useState("Untitled Form");
    const [formPurpose, setFormPurpose] = useState("Select Purpose");
    const [FormType, setFormType] = useState("Select Form Type");
    const [isPreview, setIsPreview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // console.log("forms response: ", forms)
    
    // Load form data for editing or initialize a new form
    useEffect(() => {
        if (id) {
            const existingForm = forms.find((form) => form.id === parseInt(id, 10));
            if (existingForm) {
                initializeForm(existingForm);
            } else {
                fetchFormData(id);
            }
        }
    }, [id, forms]);

    const toFieldName = (label) =>
  label
    .toLowerCase()
    .replace(/[-\s]+/g, '_')           // Replace spaces and hyphens with underscores
    .replace(/[^a-z0-9_]/g, '')        // Remove invalid characters except underscore
    .replace(/_+/g, '_')               // Collapse multiple underscores
    .replace(/^_+|_+$/g, '');

    // Fetch form data from the API
    const fetchFormData = async (formId) => {
        setIsLoading(true);
        try {
            await fetchForms();
            const form = forms.find((f) => f.id === parseInt(formId, 10));
            // console.log("form", form)
            if (form) {
                initializeForm(form);
            } else {
                setNotification({ message: "Form not found", type: "error"});
                navigate(getRoleBasedRoute("/admin/formlist", roles));
            }
        } catch (err) {
            console.error("Error fetching form data:", err);
            setNotification({ message: "Failed to fetch form data", type: "error"});
        } finally {
            setIsLoading(false);
        }
    };

    // Initialize form with data from the API or local state
    const initializeForm = (form) => {
        // console.log("form: ", form)
        setFormName(form.name || "Untitled Form");
        setFormPurpose(form.purpose || "Other");
        setFormType(form.FormType || "Other");
        const validFields = (form.fields || [])
            .filter((field) => field && typeof field === "object" && field.type)
            .map(mapAPIFieldToComponentField);
        // console.log("valid fields: ", validFields)
        setFields(validFields);
    };

    // Map API field structure to component field structure
    const mapAPIFieldToComponentField = (apiField) => ({
        id: apiField.id || uuidv4(),
        type: apiField.type,
        label: apiField.label,
        placeholder: apiField.placeholder || "",
        options: apiField.options || [],
        required: apiField.required || false,
        description: apiField.type === "form-header" ? apiField.description || "" : undefined,
        showDivider: apiField.type === "form-header" ? apiField.showDivider || false : undefined,
        image_url: apiField.type === "image" ? apiField.image_url || "" : undefined,
        alt_text: apiField.type === "image" ? apiField.alt_text || "" : undefined,
        conditions: apiField.conditions || {},
    });

    // Map component field structure to API field structure
    const mapComponentFieldToAPIField = (field) => ({
        field_name: toFieldName(field.label || "Image"), // Convert label to a valid field name
        label: field.label || "Image", 
        field_type: field.type,
        placeholder: field.placeholder,
        options: field.options,
        description: field.type === "form-header" ? field.description : undefined,
        showDivider: field.type === "form-header" ? field.showDivider : undefined,
        image_url: field.type === "image" ? field.image_url : undefined,
        alt_text: field.type === "image" ? field.alt_text : undefined,
        required: !!field.required,
        // validations: field.validations,

        // Add conditions to the API field structure
        conditions: field.conditions ? Object.keys(field.conditions).reduce((acc, key) => {
            if (field.conditions[key].enabled) {
                acc[key] = {
                    enabled: true,
                    fields: field.conditions[key].fields.map(conditionalField => ({
                        field_name: conditionalField.label,
                        label: conditionalField.label,
                        field_type: conditionalField.type,
                        required: conditionalField.required
                    }))
                };
            }
            return acc;
        }, {}) : undefined
    });

    // Add a new field to the form
    const handleAddField = (type) => {
        const newField = createField(type);
        if (newField) setFields((prevFields) => [...prevFields, newField]);
    };

    const handleAddFieldAtMarked = (fieldType) => {
        const newField = {
            id: `${fieldType}-${Date.now()}`,
            type: fieldType,
            label: `New ${fieldType}`,
        };

        setFields(prevFields => {
            const index = activeInsertIndex !== null ? activeInsertIndex : prevFields.length;
            const updatedFields = [...prevFields];
            updatedFields.splice(index, 0, newField);
            return updatedFields;
        });

        setActiveInsertIndex(null);
    };

    // Create a field based on type
    const createField = (type) => {
        const baseField = {
            id: uuidv4(),
            type,
            label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
            required: false,
        };

        switch (type) {
            case "form-header":
                return {
                    id: uuidv4(),
                    type: "form-header",
                    label: "Form Title",
                    description: "Form description goes here.",
                    required: false
                };
            case "image":
                return {
                    id: uuidv4(),
                    type: "image",
                    label: "Image",
                    image_url: "",
                    alt_text: "",
                    required: false
                };
            case "short-answer":
                return { ...baseField, placeholder: "Enter short answer" };
            case "number":
                return { ...baseField, placeholder: "Enter valid number" };
            case "mobile":
                return { ...baseField, placeholder: "Enter mobile number" };
            case "phone":
                return { ...baseField, placeholder: "Enter phone number" };
            case "email":
                return { ...baseField, placeholder: "Enter email address" };
            case "long-answer":
                return { ...baseField, placeholder: "Enter detailed response" };
            case "dropdown":
                return { ...baseField, options: ["Option 1", "Option 2"] };
            case "checkboxes":
                return { ...baseField, options: ["Checkbox 1", "Checkbox 2"] };
            case "radio":
                return { ...baseField, options: ["Option 1", "Option 2"] };
            case "date":
            case "date-time":
            case "file-upload":
                return baseField;
            case "star-rating":
                return { ...baseField, maxRating: 5 };
            case "section":
                return { ...baseField, sectionTitle: "", sectionDescription: "" };
            case "secret_token":
            case "user_password":
            case "enable_notification":
            case "e_signature":
            case "grid":
                return { ...baseField, questions: ["Question 1", "Question 2"], columns: ["1", "2", "3", "4", "5"] };
            case "repeat_group":
                return baseField;
            default:
                console.warn(`Unsupported field type: ${type}`);
                return null;
        }
    };

    // Save the form (create or update)
    const saveForm = async () => {
        if (!id && forms.some((form) => form.name.toLowerCase() === formName.toLowerCase())) {
            setError("The form name has already been taken.");
            setIsLoading(false);
            return;
        }
        
        setIsLoading(true);
        const formData = {
            id: id || null,
            formName,
            fields: fields.map(mapComponentFieldToAPIField),
            purpose: formPurpose,
            formType: FormType,
        };

        // console.log("submitting formdata: ", formData)

        try {
            if (id) {
                const response = await updateForm(id, formData);
                if (response.errors) {
                    setNotification({ message: response.message, type: "error" });
                } else if (response.message) {
                    setNotification({ message: response.message, type: "success" });
                    await fetchForms();
                    navigate("/admin/formlist", { state: { message: "Form updated successfully", tab: FormType === 'external' ? 'external' : 'internal' } });
                }
            } else {
                const response = await createForm(formData);
                if (response.errors) {  
                    setNotification({ message: response.message, type: "error" });
                } else if (response.message) {
                    setNotification({ message: response.message, type: "success" });
                    await fetchForms();
                    navigate("/admin/formlist", { state: { message: "Form Created successfully", tab: FormType === 'external' ? 'external' : 'internal' } });
                }
            }
             setError(null)
            // navigate("/admin/formlist");
        } catch (err) {
            console.error("Error saving form:", err);
            setNotification({message: "Failed to save form", type: "error"});
        } finally {
            setIsLoading(false);
        }
    };

    // Handle drag-and-drop actions
    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;

        // Handle drag from sidebar to canvas
        if (source.droppableId === SIDEBAR_DROPPABLE_ID && destination.droppableId === "formCanvas") {
            // draggableId is like 'sidebar-short-answer'
            const type = draggableId.replace("sidebar-", "");
            const newField = createField(type);
            if (newField) {
                setFields(prevFields => {
                    const updatedFields = [...prevFields];
                    updatedFields.splice(destination.index, 0, newField);
                    return updatedFields;
                });
            }
            return;
        }

        // Existing reordering logic
        if (source.droppableId === "formCanvas" && destination.droppableId === "formCanvas") {
            const reorderedFields = Array.from(fields);
            const [movedField] = reorderedFields.splice(source.index, 1);
            reorderedFields.splice(destination.index, 0, movedField);
            setFields(reorderedFields);
        }
    };

    // Download form data as a JSON file
    const downloadFormAsJSON = () => {
        const formData = { name: formName, fields, purpose: formPurpose };
        const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
            JSON.stringify(formData, null, 2)
        )}`;
        const downloadAnchor = document.createElement("a");
        downloadAnchor.setAttribute("href", dataStr);
        downloadAnchor.setAttribute("download", `${formName}.json`);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        document.body.removeChild(downloadAnchor);
    };

    const handleFormPurposeChange = (newPurpose) => {
        setFormPurpose(newPurpose);
    
        if (newPurpose === "Recruitment") {
            const newFields = [...fields];
    
            const addFieldIfNotExists = (type, label, placeholder) => {
                if (!fields.some((field) => field.type === type && field.label === label)) {
                    newFields.unshift({
                        id: uuidv4(),
                        type,
                        label,
                        placeholder,
                        required: true,
                    });
                }
            };
    
            addFieldIfNotExists("short-answer", "Department / LOB / Project");
            addFieldIfNotExists("short-answer", "Designation to be offered", "Enter contact number");
            addFieldIfNotExists("email", "Official Email Id", "Enter email address");
            addFieldIfNotExists("short-answer", "Request by - Full Name", "Enter candidate name");

            setFields(newFields);

        } else if (newPurpose === "Application") {
            const newFields = [...fields];
    
            const addFieldIfNotExists = (type, label, placeholder) => {
                if (!fields.some((field) => field.type === type && field.label === label)) {
                    newFields.unshift({
                        id: uuidv4(),
                        type,
                        label,
                        placeholder,
                        required: true,
                    });
                }
            };
            
            addFieldIfNotExists("file-upload", "Resume", "");
            addFieldIfNotExists("short-answer", "Experience", "Enter your experience");
            addFieldIfNotExists("date", "Date of Birth");
            addFieldIfNotExists("short-answer", "Contact Number", "Enter contact number");
            addFieldIfNotExists("short-answer", "Email", "Enter email address");
            addFieldIfNotExists("short-answer", "Candidate Name", "Enter candidate name");        

            setFields(newFields);
        }
        
        else {
            setFields((prevFields) =>
                prevFields.filter(
                    (field) =>
                        !(
                            (field.type === "short-answer" && field.label === "Candidate Name") ||
                            (field.type === "file-upload" && field.label === "Resume") ||
                            (field.type === "short-answer" && field.label === "Email") ||
                            (field.type === "short-answer" && field.label === "Contact Number")
                        )
                )
            );
        }
    };

    const closeBuilder = () => {
        navigate(getRoleBasedRoute("/admin/formlist", roles));
    };

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            // Enter fullscreen
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen();
            } else if (document.documentElement.webkitRequestFullscreen) {
                document.documentElement.webkitRequestFullscreen();
            } else if (document.documentElement.msRequestFullscreen) {
                document.documentElement.msRequestFullscreen();
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);
    
    if (loading || isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 flex flex-col items-center space-y-4">
                    <FaSpinner className="animate-spin text-4xl text-blue-500" />
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Loading form data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-950 font-inter relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-cyan-600/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-400/10 to-pink-600/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mx-4 mt-4 p-4 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border border-red-200 dark:border-red-800/50 rounded-2xl shadow-lg backdrop-blur-sm animate-in slide-in-from-top-2">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
                    </div>
                </div>
            )}

            {/* Notification */}
            {notification && (
                <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
            )}

            {/* Enhanced Header */}
            <header className="bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl border-b border-white/20 dark:border-gray-700/30 sticky top-0 z-20 shadow-xl shadow-black/5">
                <div className="flex items-center justify-between mx-2 py-3">
                    {/* Form Name and Purpose Row */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

                        {/* Form Name */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
                            {/* <label className="text-sm font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap tracking-wide uppercase">
                                Form Name:
                            </label> */}
                            <div className="relative group">
                                <input
                                    type="text"
                                    value={formName}
                                    onChange={(e) => setFormName(e.target.value)}
                                    className="flex-1 px-5 py-2 text-lg font-semibold bg-white/80 dark:bg-gray-800/80 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-200/50 dark:focus:ring-blue-800/50 transition-all duration-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 backdrop-blur-sm shadow-lg group-hover:shadow-xl"
                                    placeholder="Enter form name..."
                                    title="Form Name"
                                />
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </div>

                        {/* Form Purpose */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 sm:min-w-fit">
                            <div className="relative group">
                                <select
                                value={formPurpose}
                                onChange={(e) => handleFormPurposeChange(e.target.value)}
                                className="px-5 py-2 bg-white/80 dark:bg-gray-800/80 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-200/50 dark:focus:ring-blue-800/50 transition-all duration-300 text-gray-900 dark:text-white font-semibold min-w-0 sm:min-w-[160px] backdrop-blur-sm shadow-lg group-hover:shadow-xl appearance-none"
                                title="Form Purpose"
                                >
                                <option value="">Select Purpose</option>
                                {purposes
                                    ?.filter((purpose) => purpose.is_active)
                                    .map((purpose) => (
                                    <option key={purpose.id} value={purpose.name}>
                                        {purpose.name}
                                    </option>
                                ))}
                                </select>
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </div>

                        {/* form type */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 sm:min-w-fit">
                            <div className="relative group">
                                <select
                                value={FormType}
                                onChange={(e) => setFormType(e.target.value)}
                                className="px-5 py-2 bg-white/80 dark:bg-gray-800/80 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-200/50 dark:focus:ring-blue-800/50 transition-all duration-300 text-gray-900 dark:text-white font-semibold min-w-0 sm:min-w-[160px] backdrop-blur-sm shadow-lg group-hover:shadow-xl appearance-none"
                                title="Form Type"
                                >
                                <option value="">Select Form Type</option>
                                <option value="internal">Internal</option>
                                <option value="external">External</option>
                                </select>
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            </div>
                        </div>

                    </div>


                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 items-center justify-end">
                        {/* close button */}
                        <button
                            onClick={closeBuilder}
                            title="Close Form Builder"
                            className="group relative inline-flex items-center justify-center px-2.5 py-2 border-2 border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 min-w-[52px] overflow-hidden"
                        >
                            <FaTimes className="text-sm sm:text-base relative z-10" />
                            <span className="ml-1 sm:ml-2 hidden sm:inline relative z-10">Close</span>
                        </button>
                        
                        {/* full screen button */}
                        <button
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                            className="group relative inline-flex items-center justify-center px-2.5 py-2 border-2 border-purple-500 hover:border-purple-600 text-purple-500 hover:text-purple-600 font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 min-w-[52px] overflow-hidden"
                        >
                            {isFullscreen ? (
                                <FaCompress className="text-sm sm:text-base relative z-10" />
                            ) : (
                                <FaExpand className="text-sm sm:text-base relative z-10" />
                            )}
                            <span className="ml-1 sm:ml-2 hidden sm:inline relative z-10">
                                {isFullscreen ? "Exit" : "Fullscreen"}
                            </span>
                        </button>
                        
                        {/* save form button */}
                        <button
                            onClick={saveForm}
                            disabled={isLoading}
                            title="Save form"
                            className="group relative inline-flex items-center justify-center px-2.5 py-2 border-2 border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 disabled:border-gray-400 disabled:text-gray-400 font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 disabled:transform-none transition-all duration-300 min-w-[52px] overflow-hidden"
                        >
                            {isLoading ? (
                                <FaSpinner className="animate-spin text-sm sm:text-base relative z-10" />
                            ) : (
                                <FaSave className="text-sm sm:text-base relative z-10" />
                            )}
                            <span className="ml-1 sm:ml-2 hidden sm:inline relative z-10">
                                {isLoading ? "Saving..." : "Save"}
                            </span>
                        </button>
                        
                        {/* download json button */}
                        <button
                            onClick={downloadFormAsJSON}
                            title="Download form in JSON format"
                            className="group relative inline-flex items-center justify-center px-2.5 py-2 border-2 border-amber-500 hover:border-amber-600 text-amber-500 hover:text-amber-600 font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 min-w-[52px] overflow-hidden"
                        >
                            <FaDownload className="text-sm sm:text-base relative z-10" />
                            <span className="ml-1 sm:ml-2 hidden sm:inline relative z-10">Export</span>
                        </button>
                        
                        {/* preview button */}
                        <button
                            onClick={() => setIsPreview(!isPreview)}
                            title={`${isPreview ? "Edit Mode" : "Preview Mode"}`}
                            className={`group relative inline-flex items-center justify-center px-2.5 py-2 border-2 font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 min-w-[52px] overflow-hidden ${
                                isPreview
                                    ? "border-emerald-500 hover:border-emerald-600 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                                    : "border-slate-600 hover:border-slate-700 text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                            }`}
                        >
                            <div className={`absolute inset-0 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left ${
                                isPreview ? "bg-emerald-500/10" : "bg-slate-600/10"
                            }`}></div>
                            {isPreview ? <FaEdit className="text-sm sm:text-base relative z-10" /> : <FaEye className="text-sm sm:text-base relative z-10" />}
                            <span className="ml-1 sm:ml-2 hidden sm:inline relative z-10">
                                {isPreview ? "Edit" : "Preview"}
                            </span>
                        </button>
                    </div>
                    
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-1 flex overflow-hidden relative z-10">
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* Sidebar - Hidden on mobile when in preview mode */}
                    <div className={`${isPreview ? 'hidden lg:block' : 'block'} transition-all duration-500 ease-in-out`}>
                        <Sidebar onAddField={handleAddFieldAtMarked} />
                    </div>

                    {/* Canvas/Preview Area */}
                    <div className="flex-1 overflow-hidden bg-gradient-to-br from-white/20 via-blue-50/30 to-indigo-100/20 dark:from-gray-800/20 dark:via-slate-900/30 dark:to-indigo-950/20 backdrop-blur-sm">
                        <div className="h-full overflow-auto p-6">
                            <div className="max-w-6xl mx-auto">
                                {!isPreview ? (
                                    <div className="bg-white/80 dark:bg-gray-900/80 rounded-3xl shadow-2xl shadow-black/10 border border-white/20 dark:border-gray-700/30 overflow-hidden backdrop-blur-xl transform hover:scale-[1.01] transition-all duration-500">
                                        <div className="p-8">
                                            <div className="mb-8 pb-6 border-b border-gradient-to-r dark:border-gray-700 from-gray-200/50 via-blue-200/30 to-indigo-200/50 dark:from-gray-700/50 dark:via-blue-800/30 dark:to-indigo-800/50">
                                                <div className="flex items-center gap-4 mb-3">
                                                    <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-pulse"></div>
                                                    <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 dark:from-white dark:via-blue-100 dark:to-indigo-100">
                                                        Form Builder
                                                    </h3>
                                                </div>
                                                <p className="text-gray-600 dark:text-gray-400 text-lg font-medium">
                                                    Select element from the sidebar to build your form
                                                </p>
                                            </div>
                                            <FormCanvas 
                                                fields={fields} 
                                                setFields={setFields} 
                                                onMarkInsertIndex={setActiveInsertIndex}
                                                activeInsertIndex={activeInsertIndex} 
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white/80 dark:bg-gray-900/80 rounded-3xl shadow-2xl shadow-black/10 border border-white/20 dark:border-gray-700/30 overflow-hidden backdrop-blur-xl transform hover:scale-[1.01] transition-all duration-500">
                                        <div className="bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-700 px-8 py-6 relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-indigo-500/20 to-purple-600/20 animate-pulse"></div>
                                            <div className="relative z-10">
                                                <div className="flex items-center gap-4 mb-2">
                                                    <div className="w-3 h-3 bg-white/80 rounded-full animate-bounce"></div>
                                                    <h3 className="text-2xl font-black text-white">
                                                        Form Preview
                                                    </h3>
                                                </div>
                                                <p className="text-blue-100 text-lg font-medium">
                                                    This is how your form will appear to users
                                                </p>
                                            </div>
                                        </div>
                                        <div className="p-8">
                                            <FormPreview fields={fields} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </DragDropContext>
            </main>
        </div>
    );
};

export default FormBuilder;